import { useEffect, useRef, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';

/**
 * 화면 키보드 공용 도우미 — 대화방 입력줄(useComposerKeyboard)과 시트(useSheetKeyboard)가 함께 쓴다(UI 가이드 7.1).
 * 직전에 잰 키보드 높이(lastKeyboardInset)도 여기 하나로 두어 입력줄과 시트가 같은 값을 공유한다.
 */

/** 아이폰·아이패드(사파리·앱 웹뷰) */
export const isIOS = (): boolean =>
    /iPhone|iPad|iPod/.test(navigator.userAgent) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

/** iOS 새 앱(키보드 플러그인 있음) */
export const nativeKeyboard = (): boolean =>
    Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios' && Capacitor.isPluginAvailable('Keyboard');

/** 미리 올린 자리를 이만큼 기다려도 키보드가 안 뜨면(하드웨어 키보드·포커스 안 됨) 원래 자리로 */
export const PRE_INSET_TIMEOUT_MS: number = 800;

/** 화면 키보드로 칠 만한 높이 — 하드웨어 키보드의 낮은 도구 줄은 빼고 기억한다 */
export const SOFT_KEYBOARD_MIN_PX: number = 150;

/** 직전에 잰 화면 키보드 높이 — 다음에 열 때 입력줄을 미리 올릴 자리 */
let lastKeyboardInset: number = 0;

/** 직전에 잰 화면 키보드 높이 */
export const getLastKeyboardInset = (): number => lastKeyboardInset;

/** 잰 화면 키보드 높이를 기억한다 — 다음에 열 때 미리 올릴 자리 */
export const rememberKeyboardInset = (px: number): void => {
    lastKeyboardInset = px;
};

/**
 * 처음 열 때의 어림값 — 아이폰 키보드(입력 도구 줄 포함)는 화면 높이의 45~46% 쯤이다. 크게 잡아야 iOS 가 화면을 밀지 않는다.
 * 새 앱은 입력 도구 줄을 숨겨 38% 쯤이다(iPhone 17 Pro 334/874) — 크게 잡으면 키보드 높이가 올 때 입력줄이 70px 내려앉았다
 */
export const estimatedKeyboardInset = (): number => lastKeyboardInset || Math.round(window.innerHeight * (nativeKeyboard() ? 0.382 : 0.46));

/** 화면 키보드가 가린 높이(visualViewport). 창 크기 변화에도 다시 잰다(지금 깔린 앱은 visualViewport 신호 없이 창 높이만 바뀌기도 했다) */
export const useViewportInset = (): number => {
    const [inset, setInset] = useState<number>(0);
    useEffect(() => {
        const viewport: VisualViewport | null = window.visualViewport ?? null;
        if (!viewport) return undefined;
        const measure = (): void => setInset(Math.max(0, Math.round(window.innerHeight - viewport.height - viewport.offsetTop)));
        measure();
        viewport.addEventListener('resize', measure);
        viewport.addEventListener('scroll', measure);
        window.addEventListener('resize', measure);
        return () => {
            viewport.removeEventListener('resize', measure);
            viewport.removeEventListener('scroll', measure);
            window.removeEventListener('resize', measure);
        };
    }, []);
    return inset;
};

/**
 * 새 앱: 키보드 높이를 키보드가 올라오기 직전에 받는다(keyboardWillShow). 화면 줄이기가 켜진 채 키보드가 뜬 경우(포커스를 다른 길로 받은 경우)는
 * 이미 줄어든 만큼 빼서 두 번 더하지 않는다(UI 가이드 7.1). 키보드가 다 내려가면 화면 줄이기를 되돌린다(다른 화면 입력은 예전처럼).
 */
export const useNativeKeyboardHeight = (enabled: boolean, isOwnFocus: () => boolean): number => {
    const isOwnFocusRef = useRef(isOwnFocus);
    isOwnFocusRef.current = isOwnFocus;
    const [height, setHeight] = useState<number>(0);
    const fullHeight = useRef<number>(typeof window !== 'undefined' ? window.innerHeight : 0);
    const [shrink, setShrink] = useState<number>(0);
    useEffect(() => {
        if (!enabled) return undefined;
        const measure = (): void => {
            if (height === 0) fullHeight.current = Math.max(fullHeight.current, window.innerHeight);
            setShrink(Math.max(0, fullHeight.current - window.innerHeight));
        };
        measure();
        window.addEventListener('resize', measure);
        return () => window.removeEventListener('resize', measure);
    }, [enabled, height]);
    useEffect(() => {
        if (!enabled) return undefined;
        const handles: Promise<{ remove: () => Promise<void> }>[] = [
            Keyboard.addListener('keyboardWillShow', (info) => {
                if (isOwnFocusRef.current()) setHeight(info.keyboardHeight);
            }),
            Keyboard.addListener('keyboardWillHide', () => setHeight(0)),
            Keyboard.addListener('keyboardDidHide', () => {
                setHeight(0);
                Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(() => undefined);
            }),
        ];
        // 앱을 나갔다 오면 키보드 내림 신호를 못 받은 채 높이가 남을 수 있다 — 자기 칸이 포커스가 아니면 0 으로(2026-10-07 SE 새 앱:
        // 돌아온 뒤 입력줄이 키보드 높이만큼 떠 있고 아래가 빈 채 남음)
        const timers: number[] = [];
        const onVisible = (): void => {
            if (document.visibilityState !== 'visible') return;
            [100, 500].forEach((delay: number) => timers.push(window.setTimeout(() => {
                if (!isOwnFocusRef.current()) setHeight(0);
            }, delay)));
        };
        document.addEventListener('visibilitychange', onVisible);
        window.addEventListener('pageshow', onVisible);
        return () => {
            document.removeEventListener('visibilitychange', onVisible);
            window.removeEventListener('pageshow', onVisible);
            timers.forEach((timer: number) => window.clearTimeout(timer));
            handles.forEach((handle) => handle.then((h) => h.remove()).catch(() => undefined));
            Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(() => undefined);
        };
    }, [enabled]);
    return Math.max(0, height - shrink);
};
